import { useState, useEffect, useCallback, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useDrillLevel } from '../components/common/drillLevel';
import Modal from '../components/Modal';
import {
  DuplicateIcon,
  PencilIcon,
  RowAction,
  TrashIcon,
} from '../components/common/icons';
import ModelPolicy from '../components/admin/ModelPolicy';
import {
  EMPTY_PROVIDER_FILTERS,
  filterProviders,
  hasProviderFilters,
  providerCountLabel,
  type SharingFilter,
  type StateFilter,
} from '../components/llm/providerFilters';
import { useAuth } from '../contexts/AuthContext';
import type {
  LLMProvider,
  LLMProviderCreate,
  LLMProviderUpdate,
  LLMSettings,
  LLMSettingsCreate,
  LLMSettingsUpdate,
  LLMInterfaceType,
} from '../types';

// Mirrors OLLAMA_CLOUD_BASE_URL in jcseo/agents/llm.py — shown read-only so the
// value on screen is the value the agents will actually call.
export const OLLAMA_CLOUD_BASE_URL = 'https://ollama.com/v1';

/** `?provider=<id|new>` — the open provider editor. Settings reads it too, to
 *  point its back button at the providers list. */
export const PROVIDER_PARAM = 'provider';

const LLM_INTERFACE_TYPES: { value: LLMInterfaceType; label: string }[] = [
  { value: 'CHAT_OLLAMA', label: 'Ollama (self-hosted)' },
  { value: 'OLLAMA_CLOUD', label: 'Ollama Cloud' },
  { value: 'CHAT_ANTHROPIC', label: 'Anthropic' },
  { value: 'CHAT_OPENAI', label: 'OpenAI' },
  { value: 'GROQ', label: 'Groq' },
  { value: 'GEMINI', label: 'Google Gemini' },
];

// Capability flags shown both as row badges and as filter toggles.
const CAPABILITY_FILTERS: {
  key: string;
  label: string;
  cls: string;
  match: (s: LLMSettings) => boolean;
}[] = [
  { key: 'tools', label: 'Tools', cls: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300', match: (s) => s.supports_tools },
  { key: 'vision', label: 'Vision', cls: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300', match: (s) => s.supports_vision },
  { key: 'images', label: 'Image gen', cls: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300', match: (s) => s.supports_images },
  { key: 'quantized', label: 'Quantized', cls: 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300', match: (s) => s.is_highly_quantized_model },
  // Switched off: listed, but no job, customer call or bench run uses it.
  { key: 'disabled', label: 'Disabled', cls: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300', match: (s) => s.enabled === false },
];

interface LLMSettingsContentProps {
  // 'providers' is the MODELS view (legacy name); 'llm-providers' is the
  // providers (connections + keys) view; 'stages' is the pipeline.
  mode: 'providers' | 'llm-providers' | 'stages';
  isAdmin?: boolean;
}

export default function LLMSettingsContent({ mode, isAdmin = false }: LLMSettingsContentProps) {
  const { user: currentUser } = useAuth();

  const canEdit = (setting: LLMSettings) => {
    if (setting.is_system) return isAdmin;
    return setting.user_id === currentUser?.id;
  };

  // LLM Settings state
  const [settings, setSettings] = useState<LLMSettings[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState('');

  // Providers (shared connection/api-key records) state
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [providerFilters, setProviderFilters] = useState(EMPTY_PROVIDER_FILTERS);
  const [editingProvider, setEditingProvider] = useState<LLMProvider | null>(null);
  const [isCreatingProvider, setIsCreatingProvider] = useState(false);
  const [providerForm, setProviderForm] = useState<LLMProviderCreate>({
    name: '',
    llm_interface_type: 'CHAT_ANTHROPIC',
  });
  const [providerFormIsSystem, setProviderFormIsSystem] = useState(false);
  // Ollama Cloud pins its own endpoint — see OLLAMA_CLOUD_BASE_URL.
  const isOllamaCloud = providerForm.llm_interface_type === 'OLLAMA_CLOUD';
  // Gemini's endpoint is the SDK's own; a base URL is never read for it.
  const isGemini = providerForm.llm_interface_type === 'GEMINI';
  const [providerFormError, setProviderFormError] = useState('');
  const [savingProvider, setSavingProvider] = useState(false);
  const [providerTestResult, setProviderTestResult] = useState<{
    ok: boolean;
    latency_ms: number;
    response?: string | null;
    error?: string | null;
  } | null>(null);
  const [testingProvider, setTestingProvider] = useState(false);
  const [providerTestModel, setProviderTestModel] = useState('');

  // Providers table filters
  const [filterText, setFilterText] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCaps, setFilterCaps] = useState<Record<string, boolean>>({});

  // LLM Settings modal
  const [editingSettings, setEditingSettings] = useState<LLMSettings | null>(null);
  const [isCreatingSettings, setIsCreatingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState<LLMSettingsCreate>({
    provider_id: 0,
    model_name: '',
  });
  const [settingsFormError, setSettingsFormError] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  // The sub-view lives in the URL; the model form's "Manage providers" links
  // move it rather than opening a dialog.
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch LLM settings
  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError('');
    try {
      const data = await api.getLLMSettings();
      const all = data?.items || [];
      // System models first, then personal
      setSettings([...all.filter(s => s.is_system), ...all.filter(s => !s.is_system)]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load settings';
      setSettingsError(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // Fetch providers (shared connection records)
  const fetchProviders = useCallback(async () => {
    try {
      const data = await api.getLLMProviders();
      setProviders(data?.items || []);
    } catch {
      // non-fatal — the models list still renders
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchProviders();
  }, [fetchSettings, fetchProviders]);

  // Settings CRUD handlers
  const openCreateSettingsModal = () => {
    setSettingsForm({
      model_name: '',
      provider_id: providers[0]?.id ?? 0,
      description: '',
      cost_per_input_token: null,
      cost_per_output_token: null,
      price_tier: 'standard',
      rate_limit_rpm: null,
      rate_limit_rpd: null,
      rate_limit_tpm: null,
      rate_limit_tpd: null,
      supports_tools: false,
      supports_images: false,
      supports_vision: false,
      per_image_cost: null,
      max_tokens: null,
      is_highly_quantized_model: true,
      enabled: true,
    });
    setSettingsFormError('');
    setIsCreatingSettings(true);
  };

  const openEditSettingsModal = (setting: LLMSettings) => {
    setSettingsForm({
      model_name: setting.model_name,
      provider_id: setting.provider_id ?? 0,
      description: setting.description || '',
      cost_per_input_token: setting.cost_per_input_token,
      cost_per_output_token: setting.cost_per_output_token,
      price_tier: (setting.price_tier?.toLowerCase() as LLMSettingsCreate['price_tier']) ?? 'standard',
      rate_limit_rpm: setting.rate_limit_rpm ?? null,
      rate_limit_rpd: setting.rate_limit_rpd ?? null,
      rate_limit_tpm: setting.rate_limit_tpm ?? null,
      rate_limit_tpd: setting.rate_limit_tpd ?? null,
      supports_tools: setting.supports_tools,
      supports_images: setting.supports_images,
      supports_vision: setting.supports_vision,
      per_image_cost: setting.per_image_cost ?? null,
      max_tokens: setting.max_tokens ?? null,
      is_highly_quantized_model: setting.is_highly_quantized_model,
      enabled: setting.enabled ?? true,
    });
    setSettingsFormError('');
    setEditingSettings(setting);
  };

  const openCloneSettingsModal = (setting: LLMSettings) => {
    setSettingsForm({
      model_name: setting.model_name,
      provider_id: setting.provider_id ?? 0,
      // Mark the duplicate in the description (there's no longer a name field).
      description: setting.description
        ? `${setting.description} (Copy)`
        : `Copy of ${setting.display_name}`,
      cost_per_input_token: setting.cost_per_input_token,
      cost_per_output_token: setting.cost_per_output_token,
      price_tier: (setting.price_tier?.toLowerCase() as LLMSettingsCreate['price_tier']) ?? 'standard',
      rate_limit_rpm: setting.rate_limit_rpm ?? null,
      rate_limit_rpd: setting.rate_limit_rpd ?? null,
      rate_limit_tpm: setting.rate_limit_tpm ?? null,
      rate_limit_tpd: setting.rate_limit_tpd ?? null,
      supports_tools: setting.supports_tools,
      supports_images: setting.supports_images,
      supports_vision: setting.supports_vision,
      per_image_cost: setting.per_image_cost ?? null,
      max_tokens: setting.max_tokens ?? null,
      is_highly_quantized_model: setting.is_highly_quantized_model,
      enabled: setting.enabled ?? true,
    });
    setSettingsFormError('');
    setIsCreatingSettings(true);
  };

  const closeSettingsModal = () => {
    setIsCreatingSettings(false);
    setEditingSettings(null);
    setSettingsFormError('');
    setTestResult(null);
  };

  const [recalibratingExtraction, setRecalibratingExtraction] = useState(false);
  const [testingLLM, setTestingLLM] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    latency_ms: number;
    response?: string | null;
    error?: string | null;
  } | null>(null);

  const handleTestLLM = async () => {
    setTestResult(null);
    setTestingLLM(true);
    try {
      const result = await api.testLLMSettings({
        provider_id: settingsForm.provider_id || null,
        model_name: settingsForm.model_name || '',
      });
      setTestResult(result);
    } catch (err) {
      setTestResult({
        ok: false,
        latency_ms: 0,
        error: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setTestingLLM(false);
    }
  };

  const handleRecalibrateExtraction = async () => {
    if (!editingSettings) return;
    setRecalibratingExtraction(true);
    try {
      const updated = await api.recalibrateExtractionSettings(editingSettings.id);
      setEditingSettings(updated);
      fetchSettings();
    } catch (err) {
      setSettingsFormError(
        err instanceof Error ? err.message : 'Failed to recalibrate',
      );
    } finally {
      setRecalibratingExtraction(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settingsForm.provider_id) {
      setSettingsFormError('Select a provider (create one first if none exist)');
      return;
    }
    if (!settingsForm.model_name.trim()) {
      setSettingsFormError('Model name is required');
      return;
    }

    setSavingSettings(true);
    setSettingsFormError('');
    try {
      if (editingSettings) {
        const updateData: LLMSettingsUpdate = { ...settingsForm };
        await api.updateLLMSettings(editingSettings.id, updateData);
      } else {
        // Shared or personal follows the PROVIDER — nothing to say here.
        await api.createLLMSettings(settingsForm);
      }
      closeSettingsModal();
      fetchSettings();
    } catch (err) {
      setSettingsFormError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDeleteSettings = async (setting: LLMSettings) => {
    if (!window.confirm(`Delete LLM settings "${setting.display_name}"?`)) return;
    try {
      await api.deleteLLMSettings(setting.id);
      fetchSettings();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  // Provider CRUD handlers
  const canEditProvider = (p: LLMProvider) => {
    if (p.is_system) return isAdmin;
    return p.user_id === currentUser?.id;
  };

  // Providers are their own sub-view now (`?tab=ai&sub=providers`); the
  // links inside the model form go there.
  const openManageProviders = () => {
    setEditingProvider(null);
    setIsCreatingProvider(false);
    setProviderFormError('');
    setProviderTestResult(null);
    closeSettingsModal();
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'ai');
    params.set('sub', 'providers');
    params.delete(PROVIDER_PARAM);
    setSearchParams(params, { replace: true });
  };

  // The open editor lives in the URL as its own history entry, so Back returns
  // to the list rather than leaving Settings. Clicks move the URL; the effect
  // below loads the form from it.
  const providerLevel = useDrillLevel(PROVIDER_PARAM);
  const openCreateProvider = () => providerLevel.open('new');
  const openEditProvider = (p: LLMProvider) => providerLevel.open(String(p.id));

  useEffect(() => {
    if (mode !== 'llm-providers') return;
    const v = providerLevel.value;
    if (v === null) {
      if (editingProvider || isCreatingProvider) closeProviderEditor();
    } else if (v === 'new') {
      if (!isCreatingProvider) loadNewProvider();
    } else {
      const p = providers.find((x) => String(x.id) === v);
      if (p && editingProvider?.id !== p.id) loadProviderEditor(p);
    }
    // Only the URL and the loaded list decide what is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, providerLevel.value, providers]);

  const loadNewProvider = () => {
    setProviderForm({ name: '', llm_interface_type: 'CHAT_ANTHROPIC', base_url: '', api_key: '' });
    setProviderFormIsSystem(false);
    setProviderFormError('');
    setProviderTestResult(null);
    setProviderTestModel('');
    setEditingProvider(null);
    setIsCreatingProvider(true);
  };

  const loadProviderEditor = (p: LLMProvider) => {
    setProviderForm({
      name: p.name,
      llm_interface_type: p.llm_interface_type,
      base_url: p.base_url || '',
      api_key: '', // blank keeps the stored key
      rate_limit_rpm: p.rate_limit_rpm,
      rate_limit_rpd: p.rate_limit_rpd,
      rate_limit_tpm: p.rate_limit_tpm,
      rate_limit_tpd: p.rate_limit_tpd,
      cost_basis: p.cost_basis ?? 'metered',
      enabled: p.enabled ?? true,
      bench_enabled: p.bench_enabled ?? false,
      plan_monthly_usd: p.plan_monthly_usd ?? null,
      quota_tokens_day: p.quota_tokens_day ?? null,
      quota_tokens_week: p.quota_tokens_week ?? null,
      quota_tokens_month: p.quota_tokens_month ?? null,
    });
    setProviderFormIsSystem(p.is_system);
    setProviderFormError('');
    setProviderTestResult(null);
    setProviderTestModel('');
    setIsCreatingProvider(false);
    setEditingProvider(p);
  };

  const closeProviderEditor = () => {
    setEditingProvider(null);
    setIsCreatingProvider(false);
    setProviderFormError('');
    setProviderTestResult(null);
  };

  const handleSaveProvider = async () => {
    if (!providerForm.name?.trim()) {
      setProviderFormError('Provider name is required');
      return;
    }
    setSavingProvider(true);
    setProviderFormError('');
    try {
      if (editingProvider) {
        // Ownership moves through its own admin-only endpoint, like models do.
        if (providerFormIsSystem !== editingProvider.is_system) {
          await api.setLLMProviderSystemStatus(editingProvider.id, providerFormIsSystem);
        }
        const upd: LLMProviderUpdate = {
          name: providerForm.name,
          llm_interface_type: providerForm.llm_interface_type,
          base_url: providerForm.base_url ?? null,
          rate_limit_rpm: providerForm.rate_limit_rpm ?? null,
          rate_limit_rpd: providerForm.rate_limit_rpd ?? null,
          rate_limit_tpm: providerForm.rate_limit_tpm ?? null,
          rate_limit_tpd: providerForm.rate_limit_tpd ?? null,
          cost_basis: providerForm.cost_basis ?? 'metered',
          enabled: providerForm.enabled ?? true,
          bench_enabled: providerForm.bench_enabled ?? false,
          plan_monthly_usd: providerForm.plan_monthly_usd ?? null,
          quota_tokens_day: providerForm.quota_tokens_day ?? null,
          quota_tokens_week: providerForm.quota_tokens_week ?? null,
          quota_tokens_month: providerForm.quota_tokens_month ?? null,
        };
        if (providerForm.api_key) upd.api_key = providerForm.api_key;
        await api.updateLLMProvider(editingProvider.id, upd);
      } else {
        await api.createLLMProvider({ ...providerForm, is_system: providerFormIsSystem });
      }
      providerLevel.close();
      fetchProviders();
      fetchSettings(); // provider name/label may have changed
    } catch (err) {
      setProviderFormError(err instanceof Error ? err.message : 'Failed to save provider');
    } finally {
      setSavingProvider(false);
    }
  };

  const handleDeleteProvider = async (p: LLMProvider) => {
    if (p.model_count > 0) {
      alert(`Cannot delete "${p.name}": ${p.model_count} model(s) still use it.`);
      return;
    }
    if (!window.confirm(`Delete provider "${p.name}"?`)) return;
    try {
      await api.deleteLLMProvider(p.id);
      fetchProviders();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete provider');
    }
  };

  const handleTestProvider = async () => {
    setProviderTestResult(null);
    setTestingProvider(true);
    try {
      const result = await api.testLLMSettings({
        provider_id: editingProvider?.id ?? null,
        model_name: providerTestModel || '',
        llm_interface_type: providerForm.llm_interface_type,
        base_url: providerForm.base_url ?? null,
        api_key: providerForm.api_key ?? null,
      });
      setProviderTestResult(result);
    } catch (err) {
      setProviderTestResult({
        ok: false,
        latency_ms: 0,
        error: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setTestingProvider(false);
    }
  };

  const providerLabel = (p: LLMProvider) => {
    const iface = LLM_INTERFACE_TYPES.find((t) => t.value === p.llm_interface_type)?.label || p.llm_interface_type;
    return `${p.name} (${iface})${p.is_system ? ' [System]' : ''}`;
  };

  // Distinct providers present in the table, for the provider filter dropdown.
  const providerOptions = Array.from(
    new Set(settings.map((s) => s.provider).filter((p): p is string => !!p)),
  ).sort((a, b) => a.localeCompare(b));

  const activeCaps = CAPABILITY_FILTERS.filter((c) => filterCaps[c.key]);
  const filtersActive = !!(
    filterText.trim() ||
    filterProvider ||
    filterType ||
    activeCaps.length
  );

  const shownProviders = filterProviders(providers, providerFilters);

  const filteredSettings = settings.filter((s) => {
    if (filterProvider && (s.provider || '') !== filterProvider) return false;
    if (filterType && s.llm_interface_type !== filterType) return false;
    // Capability toggles combine with AND — a row must satisfy every selected flag.
    if (activeCaps.some((c) => !c.match(s))) return false;
    if (filterText.trim()) {
      const q = filterText.trim().toLowerCase();
      const haystack = [s.display_name, s.model_name, s.provider, s.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  return (
    <div>
      {/* LLM Providers */}
      {mode === 'providers' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Your LLM Models</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Each model runs on a provider - {' '}
                <button type="button" onClick={openManageProviders} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                  manage providers
                </button>{' '}
                ({providers.length}) to set connections &amp; API keys once.
              </p>
            </div>
            <button
              onClick={openCreateSettingsModal}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm whitespace-nowrap"
            >
              Add Model
            </button>
          </div>

          {settingsError && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
              {settingsError}
            </div>
          )}

          {settingsLoading ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>
          ) : settings.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 border rounded-lg p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400 mb-4">No LLM models configured yet</p>
              <button
                onClick={openCreateSettingsModal}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Add Your First Model
              </button>
            </div>
          ) : (
            <>
            <div className="mb-4 space-y-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Search model, provider, description…"
                  className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <select
                  value={filterProvider}
                  onChange={(e) => setFilterProvider(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All providers</option>
                  {providerOptions.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All types</option>
                  {LLM_INTERFACE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Capabilities</span>
                {CAPABILITY_FILTERS.map((c) => {
                  const on = !!filterCaps[c.key];
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setFilterCaps((prev) => ({ ...prev, [c.key]: !prev[c.key] }))}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                        on ? `${c.cls} border-transparent` : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {c.label}
                    </button>
                  );
                })}
                {filtersActive && (
                  <button
                    onClick={() => { setFilterText(''); setFilterProvider(''); setFilterType(''); setFilterCaps({}); }}
                    className="ml-auto px-3 py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            {filteredSettings.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 border rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
                No providers match the current filters.
              </div>
            ) : (
            <div className="bg-white dark:bg-gray-800 border rounded-lg overflow-x-auto">
              <table className="w-full min-w-0">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Model</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden sm:table-cell">Provider</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden lg:table-cell">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden sm:table-cell">Capabilities</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredSettings.map((setting) => (
                    <tr key={setting.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-gray-100">{setting.model_name}</span>
                          {setting.is_system && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded">System</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 sm:hidden">{setting.provider || '-'}</div>
                        {setting.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[240px] hidden sm:block">{setting.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 hidden sm:table-cell">{setting.provider || '-'}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded">
                          {LLM_INTERFACE_TYPES.find((t) => t.value === setting.llm_interface_type)?.label || setting.llm_interface_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {CAPABILITY_FILTERS.filter((c) => c.match(setting)).map((c) => (
                            <span key={c.key} className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${c.cls}`}>
                              {c.label}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          {canEdit(setting) && (
                            <RowAction title="Edit" onClick={() => openEditSettingsModal(setting)}>
                              <PencilIcon />
                            </RowAction>
                          )}
                          <RowAction title="Clone" tone="green" onClick={() => openCloneSettingsModal(setting)}>
                            <DuplicateIcon />
                          </RowAction>
                          {canEdit(setting) && (
                            <RowAction title="Delete" tone="red" onClick={() => handleDeleteSettings(setting)}>
                              <TrashIcon />
                            </RowAction>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            </>
          )}
        </div>
      )}

      {/* Stage Configuration */}
      {/* WHICH MODELS MAY DO EACH JOB — a versioned policy, its own component. */}
      {mode === 'stages' && <ModelPolicy />}

      {/* LLM Settings Modal */}
      <Modal
        isOpen={isCreatingSettings || !!editingSettings}
        onClose={closeSettingsModal}
        title={editingSettings ? 'Edit LLM Model' : 'Add LLM Model'}
      >
        <div className="space-y-4">
          {settingsFormError && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">{settingsFormError}</div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Provider</label>
              <button
                type="button"
                onClick={openManageProviders}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                Manage providers
              </button>
            </div>
            {providers.length === 0 ? (
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-3 py-2">
                No providers yet.{' '}
                <button type="button" onClick={openManageProviders} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                  Create one
                </button>{' '}
                to set the interface, base URL and API key once.
              </div>
            ) : (
              <select
                value={settingsForm.provider_id || 0}
                onChange={(e) => setSettingsForm({ ...settingsForm, provider_id: Number(e.target.value) })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={0} disabled>- Select a provider -</option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>{providerLabel(p)}</option>
                ))}
              </select>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The connection (interface, base URL, API key) comes from the provider.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Model Name</label>
            <input
              type="text"
              autoComplete="off"
              value={settingsForm.model_name}
              onChange={(e) => setSettingsForm({ ...settingsForm, model_name: e.target.value })}
              placeholder="e.g., llama3.2, claude-3-sonnet"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description (optional)</label>
            <textarea
              value={settingsForm.description || ''}
              onChange={(e) => setSettingsForm({ ...settingsForm, description: e.target.value })}
              rows={2}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Sharing is the provider's property: what is shared is the key
              underneath. A model on a shared provider is shared, full stop. */}
          {(() => {
            const prov = providers.find((p) => p.id === settingsForm.provider_id);
            if (!prov) return null;
            return (
              <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="model-sharing-note">
                {prov.is_system
                  ? 'Shared with every user, because its provider is shared.'
                  : 'Personal - only you can use it. Share the provider to share its models.'}
              </p>
            );
          })()}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Input cost ($ / 1M tokens)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={settingsForm.cost_per_input_token ?? ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, cost_per_input_token: e.target.value ? Number(e.target.value) : null })}
                placeholder="e.g., 3.00"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Output cost ($ / 1M tokens)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={settingsForm.cost_per_output_token ?? ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, cost_per_output_token: e.target.value ? Number(e.target.value) : null })}
                placeholder="e.g., 15.00"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* THE TIER is what a customer PAYS for this model's tokens; the two
              cost fields above are what it costs US. Both are admin-only facts,
              and the rate card prices the tier, never the model. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Price tier (what customers pay)</label>
            <select
              value={settingsForm.price_tier ?? 'standard'}
              onChange={(e) => setSettingsForm({ ...settingsForm, price_tier: e.target.value as LLMSettingsCreate['price_tier'] })}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="economy">Economy</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
            </select>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" title="Requests per minute">RPM</label>
              <input
                type="number"
                min="0"
                value={settingsForm.rate_limit_rpm ?? ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, rate_limit_rpm: e.target.value ? Number(e.target.value) : null })}
                placeholder="-"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" title="Requests per day">RPD</label>
              <input
                type="number"
                min="0"
                value={settingsForm.rate_limit_rpd ?? ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, rate_limit_rpd: e.target.value ? Number(e.target.value) : null })}
                placeholder="-"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" title="Tokens per minute">TPM</label>
              <input
                type="number"
                min="0"
                value={settingsForm.rate_limit_tpm ?? ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, rate_limit_tpm: e.target.value ? Number(e.target.value) : null })}
                placeholder="-"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" title="Tokens per day">TPD</label>
              <input
                type="number"
                min="0"
                value={settingsForm.rate_limit_tpd ?? ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, rate_limit_tpd: e.target.value ? Number(e.target.value) : null })}
                placeholder="-"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" title="Override the provider default. Required for models with lower limits (e.g. claude-3-haiku-20240307: 4096)">Max output tokens</label>
            <input
              type="number"
              min="1"
              value={settingsForm.max_tokens ?? ''}
              onChange={(e) => setSettingsForm({ ...settingsForm, max_tokens: e.target.value ? Number(e.target.value) : null })}
              placeholder="- (use provider default)"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={settingsForm.enabled ?? true}
              onChange={(e) => setSettingsForm({ ...settingsForm, enabled: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
            />
            <span className="text-gray-700 dark:text-gray-300">
              Enabled (off: stays listed, but no job, customer call or stage bench runs on it)
            </span>
          </label>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={settingsForm.supports_tools ?? false}
              onChange={(e) => setSettingsForm({ ...settingsForm, supports_tools: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
            />
            <span className="text-gray-700 dark:text-gray-300">Supports tools</span>
          </label>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={settingsForm.supports_vision ?? false}
              onChange={(e) => setSettingsForm({ ...settingsForm, supports_vision: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
            />
            <span className="text-gray-700 dark:text-gray-300">
              Supports vision (reads image input - e.g. Qwen2.5-VL, gpt-4o)
            </span>
          </label>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={settingsForm.supports_images ?? false}
              onChange={(e) => setSettingsForm({ ...settingsForm, supports_images: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
            />
            <span className="text-gray-700 dark:text-gray-300">
              Image generation model (e.g. OpenAI GPT Image 1.5)
            </span>
          </label>

          {settingsForm.supports_images && (
            <div className="ml-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Flat cost per image ($) - leave blank for OpenAI
              </label>
              <input
                type="number"
                step="0.001"
                min="0"
                value={settingsForm.per_image_cost ?? ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, per_image_cost: e.target.value ? Number(e.target.value) : null })}
                placeholder="only for flat-rate providers (e.g. FLUX)"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                OpenAI GPT Image is token-billed - leave this blank and set the
                input/output token costs above; cost is tracked automatically.
              </p>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={settingsForm.is_highly_quantized_model ?? true}
              onChange={(e) => setSettingsForm({ ...settingsForm, is_highly_quantized_model: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
            />
            <span className="text-gray-700 dark:text-gray-300">Highly quantized model</span>
          </label>

          {editingSettings && (
            <div className="border-t pt-4 mt-2">
              <div className="flex items-baseline justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Auto-tuned extraction settings
                </h4>
                <button
                  type="button"
                  onClick={handleRecalibrateExtraction}
                  disabled={recalibratingExtraction}
                  className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                  title="Re-derive these values from recent extraction call telemetry. Needs ≥ 20 calls."
                >
                  {recalibratingExtraction ? 'Recalibrating…' : 'Recalibrate'}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Managed by the system based on observed run telemetry. Blank
                values mean defaults are still in effect.
              </p>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <dt className="text-gray-600 dark:text-gray-300">Chunk max chars</dt>
                <dd className="text-gray-900 dark:text-gray-100 font-mono">
                  {editingSettings.extraction_chunk_max_chars ?? '- (default 6000)'}
                </dd>
                <dt className="text-gray-600 dark:text-gray-300">Require evidence</dt>
                <dd className="text-gray-900 dark:text-gray-100 font-mono">
                  {editingSettings.extraction_require_evidence === null ||
                  editingSettings.extraction_require_evidence === undefined
                    ? '- (default on)'
                    : editingSettings.extraction_require_evidence
                      ? 'on'
                      : 'off'}
                </dd>
                <dt className="text-gray-600 dark:text-gray-300">Parallel chunks</dt>
                <dd className="text-gray-900 dark:text-gray-100 font-mono">
                  {editingSettings.extraction_parallel_chunks ?? '- (default 1)'}
                </dd>
                <dt className="text-gray-600 dark:text-gray-300">Max chunks / page</dt>
                <dd className="text-gray-900 dark:text-gray-100 font-mono">
                  {editingSettings.extraction_max_chunks_per_page ?? '- (default 10)'}
                </dd>
                <dt className="text-gray-600 dark:text-gray-300">Last calibrated</dt>
                <dd className="text-gray-900 dark:text-gray-100 font-mono">
                  {editingSettings.extraction_last_calibrated_at
                    ? new Date(editingSettings.extraction_last_calibrated_at).toLocaleString()
                    : 'never'}
                </dd>
              </dl>
            </div>
          )}

          {testResult && (
            <div
              className={`text-xs px-3 py-2 rounded border ${
                testResult.ok
                  ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
                  : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
              }`}
            >
              <div className="font-medium">
                {testResult.ok ? '✓ OK' : '✗ Failed'} ({testResult.latency_ms} ms)
              </div>
              {testResult.ok && testResult.response && (
                <div className="mt-1 font-mono text-[11px] break-words">
                  reply: {testResult.response}
                </div>
              )}
              {!testResult.ok && testResult.error && (
                <div className="mt-1 font-mono text-[11px] break-words">
                  {testResult.error}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between items-center gap-3 pt-4">
            <button
              type="button"
              onClick={handleTestLLM}
              disabled={testingLLM || !settingsForm.model_name}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              title="Send a tiny 'reply with: OK' completion to verify the provider config is reachable. Doesn't save."
            >
              {testingLLM ? 'Testing…' : 'Test'}
            </button>
            <div className="flex gap-3">
              <button
                onClick={closeSettingsModal}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {savingSettings ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Providers: the connections and keys models run on. A sub-view of the
          AI hub — the one every user gets, since anyone may register a key. */}
      {mode === 'llm-providers' && (
        <div className="space-y-4" data-testid="providers-panel">
          {/* Editor (create/edit) */}
          {(isCreatingProvider || editingProvider) ? (
            <div className="space-y-4">
              {providerFormError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">{providerFormError}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                <input
                  type="text"
                  value={providerForm.name}
                  onChange={(e) => setProviderForm({ ...providerForm, name: e.target.value })}
                  placeholder="e.g., Anthropic, OpenAI, Local Ollama"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Interface Type</label>
                <select
                  value={providerForm.llm_interface_type}
                  onChange={(e) => setProviderForm({ ...providerForm, llm_interface_type: e.target.value as LLMInterfaceType })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {LLM_INTERFACE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              {/* Ollama Cloud has exactly ONE correct endpoint, and the `/v1` suffix is
                  the whole point of it — it selects the OpenAI-compatible API, which is
                  the one that returns native tool calls. A row missing it doesn't fail,
                  it silently degrades, so the field is not offered as a choice. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Base URL {isOllamaCloud || isGemini ? '(fixed)' : '(optional)'}
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  disabled={isOllamaCloud || isGemini}
                  value={isOllamaCloud ? OLLAMA_CLOUD_BASE_URL : isGemini ? '' : providerForm.base_url || ''}
                  onChange={(e) => setProviderForm({ ...providerForm, base_url: e.target.value })}
                  placeholder={isGemini ? 'Not used' : 'e.g., http://localhost:11434'}
                  className={`mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    isOllamaCloud || isGemini
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : ''
                  }`}
                />
                {isOllamaCloud && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Set for you. This endpoint returns native tool calls; the plain
                    Ollama interface loses them against the hosted service.
                  </p>
                )}
                {isGemini && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Uses Google&apos;s Gemini API directly. Create the API key in Google AI Studio.
                  </p>
                )}
              </div>
              {/* THE COST BOOK, provider side. How this vendor costs us is an
                  admin fact for the margin screen: a metered vendor bills per
                  token at the models' rates; a flat plan (Ollama Cloud) buys a
                  quota for a fee, so its models' rates are IMPUTED; a self-hosted
                  GPU is imputed from $/hour. Customers never see any of it. */}
              {/* The two facts about a key that are not its connection: who
                  may use it, and whether it is in play. They read as a pair
                  and belong together. */}
              <div className="space-y-2">
                {isAdmin && (
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={providerFormIsSystem}
                      onChange={(e) => setProviderFormIsSystem(e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Shared - every user, and every model on it, runs on this key
                    </span>
                  </label>
                )}
                {/* Paused: kept on file with its key, out of the pool. */}
                <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={providerForm.enabled ?? true}
                    onChange={(e) => setProviderForm({ ...providerForm, enabled: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                  />
                  Enabled - in the pool for the jobs its models are allowed on
                </label>
                {/* Independent of Enabled: measure a new provider on the stage
                    bench while nothing live runs on it, then switch it on. */}
                <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={providerForm.bench_enabled ?? false}
                    onChange={(e) => setProviderForm({ ...providerForm, bench_enabled: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                  />
                  On the stage bench - benchmark its models, even while it is not enabled (shared providers only)
                </label>
              </div>
              {isAdmin && (
                <div className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-3 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Cost basis (ours)</label>
                      <select
                        value={providerForm.cost_basis ?? 'metered'}
                        onChange={(e) => setProviderForm({ ...providerForm, cost_basis: e.target.value as LLMProviderCreate['cost_basis'] })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="metered">Metered - per token at the models' rates</option>
                        <option value="flat_plan">Flat plan - a fee buys a quota</option>
                        <option value="self_hosted">Self-hosted - our own or rented GPU</option>
                      </select>
                    </div>
                    {providerForm.cost_basis && providerForm.cost_basis !== 'metered' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {providerForm.cost_basis === 'flat_plan' ? 'Plan fee ($ / month)' : 'Hosting ($ / month)'}
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={providerForm.plan_monthly_usd ?? ''}
                          onChange={(e) => setProviderForm({ ...providerForm, plan_monthly_usd: e.target.value ? Number(e.target.value) : null })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {(
                      [
                        ['quota_tokens_day', 'Token quota / day'],
                        ['quota_tokens_week', 'Token quota / week'],
                        ['quota_tokens_month', 'Token quota / month'],
                      ] as const
                    ).map(([field, label]) => (
                      <div key={field}>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
                        <input
                          type="number"
                          min="0"
                          value={providerForm[field] ?? ''}
                          onChange={(e) => setProviderForm({ ...providerForm, [field]: e.target.value ? Number(e.target.value) : null })}
                          placeholder="-"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Quotas are what the margin screen gauges usage against; leave blank when the vendor has none.
                    For a flat plan or self-hosted provider, set each model's dollars-per-million to the imputed cost.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  API Key {editingProvider && '(leave blank to keep existing)'}
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  style={{ WebkitTextSecurity: 'disc' } as React.CSSProperties}
                  value={providerForm.api_key || ''}
                  onChange={(e) => setProviderForm({ ...providerForm, api_key: e.target.value })}
                  placeholder={editingProvider?.has_api_key ? '•••••••• (stored)' : '••••••••'}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Editing the key here re-keys every model on this provider at once.
                </p>
              </div>

              {/* Account/tier-wide rate limits. Set once here (e.g. a Groq free-tier
                  account's 8000 TPM) and every model on this provider inherits the
                  ceiling — a run uses the LOWER of this and the model's own limit. */}
              <div className="border-t pt-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Account rate limits (optional)</label>
                <p className="mt-0.5 mb-2 text-xs text-gray-500 dark:text-gray-400">
                  Applied to every model on this provider. A run uses the lower of these and the model's own limits. Leave blank for no cap.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {([
                    ['rate_limit_rpm', 'Requests / min (RPM)'],
                    ['rate_limit_tpm', 'Tokens / min (TPM)'],
                    ['rate_limit_rpd', 'Requests / day (RPD)'],
                    ['rate_limit_tpd', 'Tokens / day (TPD)'],
                  ] as const).map(([key, label]) => (
                    <div key={key}>
                      <label className="block text-xs text-gray-500 dark:text-gray-400">{label}</label>
                      <input
                        type="number"
                        min="0"
                        value={providerForm[key] ?? ''}
                        onChange={(e) =>
                          setProviderForm({
                            ...providerForm,
                            [key]: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                        placeholder="-"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Test the connection with a sample model */}
              <div className="border-t pt-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Test with model (optional)</label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="text"
                    value={providerTestModel}
                    onChange={(e) => setProviderTestModel(e.target.value)}
                    placeholder="e.g., claude-sonnet-4-5"
                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={handleTestProvider}
                    disabled={testingProvider || !providerTestModel}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 whitespace-nowrap"
                  >
                    {testingProvider ? 'Testing…' : 'Test'}
                  </button>
                </div>
                {providerTestResult && (
                  <div
                    className={`mt-2 text-xs px-3 py-2 rounded border ${
                      providerTestResult.ok
                        ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
                        : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
                    }`}
                  >
                    <div className="font-medium">
                      {providerTestResult.ok ? '✓ OK' : '✗ Failed'} ({providerTestResult.latency_ms} ms)
                    </div>
                    {providerTestResult.error && (
                      <div className="mt-1 font-mono text-[11px] break-words">{providerTestResult.error}</div>
                    )}
                    {providerTestResult.ok && providerTestResult.response && (
                      <div className="mt-1 font-mono text-[11px] break-words">reply: {providerTestResult.response}</div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={providerLevel.close} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                  Cancel
                </button>
                <button
                  onClick={handleSaveProvider}
                  disabled={savingProvider}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingProvider ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center gap-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {isAdmin
                    ? 'The connections and keys models run on. A shared provider shares every model on it with every user; a personal one is yours alone.'
                    : 'Your own provider keys. Where the house mapping has a tested model on your vendor, your work runs on your key at the own-key rate. Which model handles each step stays ours.'}
                </p>
                <button
                  onClick={openCreateProvider}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  Add Provider
                </button>
              </div>
              {providers.length === 0 ? (
                <div className="bg-gray-50 dark:bg-gray-800 border rounded-lg p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                  No providers yet. Add one to enter its API key once.
                </div>
              ) : (
                <>
                {/* Filters, in one row above the list. Worth it once there are
                    a dozen: "which of these does the house actually run on"
                    is two clicks (Shared + Enabled) rather than a read. */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={providerFilters.text}
                    onChange={(e) => setProviderFilters({ ...providerFilters, text: e.target.value })}
                    placeholder="Search name or host…"
                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <select
                    value={providerFilters.kind}
                    onChange={(e) => setProviderFilters({ ...providerFilters, kind: e.target.value })}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">All types</option>
                    {LLM_INTERFACE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  {isAdmin && (
                    <select
                      value={providerFilters.sharing}
                      onChange={(e) => setProviderFilters({ ...providerFilters, sharing: e.target.value as SharingFilter })}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Shared and personal</option>
                      <option value="shared">Shared only</option>
                      <option value="personal">Personal only</option>
                    </select>
                  )}
                  <select
                    value={providerFilters.state}
                    onChange={(e) => setProviderFilters({ ...providerFilters, state: e.target.value as StateFilter })}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Enabled and paused</option>
                    <option value="enabled">Enabled only</option>
                    <option value="paused">Paused only</option>
                  </select>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>{providerCountLabel(shownProviders.length, providers.length, providerFilters)}</span>
                  {hasProviderFilters(providerFilters) && (
                    <button
                      type="button"
                      onClick={() => setProviderFilters(EMPTY_PROVIDER_FILTERS)}
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
                {shownProviders.length === 0 ? (
                  <div className="bg-gray-50 dark:bg-gray-800 border rounded-lg p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                    No provider matches these filters.
                  </div>
                ) : (
                <div className="border rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                  {shownProviders.map((p) => (
                    <div
                      key={p.id}
                      className={`px-3 py-2.5 flex items-center justify-between ${
                        canEditProvider(p) ? 'hover:bg-gray-50 dark:hover:bg-gray-700/40' : ''
                      }`}
                    >
                      {/* The whole row opens the editor, not just the pencil.
                          A button rather than a click handler on the div, so it
                          reaches the keyboard too. */}
                      <button
                        type="button"
                        disabled={!canEditProvider(p)}
                        onClick={() => openEditProvider(p)}
                        title={canEditProvider(p) ? `Edit ${p.name}` : undefined}
                        className="min-w-0 flex-1 text-left disabled:cursor-default"
                      >
                        <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                          {p.name}
                          {p.is_system && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded">System</span>
                          )}
                          {/* Both states, not just the odd one: "is this key
                              live?" is the question the table is read for. */}
                          {p.enabled === false ? (
                            <span
                              title="Paused - kept on file with its key, but nothing runs on it."
                              className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                            >
                              Paused
                            </span>
                          ) : (
                            <span
                              title="Enabled - models on it can run."
                              className="px-1.5 py-0.5 text-[10px] font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded"
                            >
                              Enabled
                            </span>
                          )}
                          {p.bench_enabled && (
                            <span
                              title="On the stage bench - models on it are benchmarked, whether or not it is enabled."
                              className="px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded"
                            >
                              Bench
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {LLM_INTERFACE_TYPES.find((t) => t.value === p.llm_interface_type)?.label || p.llm_interface_type}
                          {p.base_url ? ` • ${p.base_url}` : ''}
                          {' • '}{p.has_api_key ? 'key set' : 'no key'}
                          {' • '}{p.model_count} model{p.model_count === 1 ? '' : 's'}
                        </div>
                      </button>
                      <div className="flex gap-1 shrink-0">
                        {canEditProvider(p) && (
                          <RowAction title="Edit" onClick={() => openEditProvider(p)}>
                            <PencilIcon />
                          </RowAction>
                        )}
                        {canEditProvider(p) && (
                          <RowAction
                            title={p.model_count > 0 ? 'In use by models' : 'Delete'}
                            tone="red"
                            disabled={p.model_count > 0}
                            onClick={() => handleDeleteProvider(p)}
                          >
                            <TrashIcon />
                          </RowAction>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
